import { create } from "zustand";

export type CameraMode = "camera" | "orbit" | "player" | "bird";

export interface GameState {
  checkpoint: boolean;
  fxVolume: number;
  isControllable: boolean;
  isMuted: boolean;
  lapTimes: number[];
  lapTimeStart: number;
  lights: number;
  mode: CameraMode;
  musicVolume: number;
  updateCheckpoint: (checkpoint: boolean) => void;
  updateFxVolume: (volume: number) => void;
  updateLights: (lights: number) => void;
  updateMode: (mode: CameraMode) => void;
  updateMuted: (isMuted: boolean) => void;
  updateMusicVolume: (volume: number) => void;
}

export const useGameStore = create<GameState>()((set) => ({
  checkpoint: false,
  fxVolume: 0.3,
  isControllable: false,
  isMuted: true,
  lapTimes: [],
  lapTimeStart: 0,
  lights: 0,
  mode: "camera",
  musicVolume: 0,
  updateCheckpoint: (checkpoint) =>
    set((state) => {
      if (checkpoint !== state.checkpoint) {
        if (checkpoint) {
          return {
            ...state,
            checkpoint,
          };
        }

        const lapTimes = state.lapTimes.reduce(
          (previousValue, currentValue) => {
            const lastValue = previousValue.pop();

            if (lastValue) {
              if (currentValue < lastValue) {
                return [...previousValue, currentValue, lastValue];
              }

              return [...previousValue, lastValue, currentValue];
            }

            return [...previousValue, currentValue];
          },
          [new Date().valueOf() - state.lapTimeStart]
        );

        return {
          ...state,
          checkpoint,
          lapTimes: lapTimes.slice(0, Math.min(3, lapTimes.length)),
          lapTimeStart: new Date().valueOf(),
        };
      }

      return state;
    }),
  updateLights: (lights) =>
    set((state) => {
      if (lights === 2) {
        return {
          ...state,
          isControllable: true,
          lapTimeStart: new Date().valueOf(),
          lights,
        };
      }

      return {
        ...state,
        lights,
      };
    }),
  updateMode: (mode) =>
    set((state) => ({
      ...state,
      checkpoint: false,
      isControllable: false,
      lapTimeStart: new Date().valueOf(),
      lights: 0,
      mode,
      isMuted: !(mode === "player"),
    })),
  updateMuted: (isMuted) =>
    set((state) => ({
      ...state,
      isMuted,
    })),
  updateFxVolume: (fxVolume) =>
    set((state) => ({
      ...state,
      fxVolume,
    })),
  updateMusicVolume: (musicVolume) =>
    set((state) => ({
      ...state,
      musicVolume,
    })),
}));
